import { formatDate, formatDateAndTime, t } from "@hakili/i18n";
import { BANK_BORDER, BANK_FS_BODY, BANK_FS_LEAD, BANK_FS_READ, BANK_TEXT, BANK_TEXT_2 } from "@hakili/ui";
import { Check } from "lucide-react";
import type { ReactElement } from "react";

import { DASH } from "./dossiers.js";

/** The typed dates « Le parcours » reads; `conditionsLiftedAt` is `undefined` when the read does not deliver the field. */
export interface ParcoursDates {
  readonly submittedAt: string | null;
  readonly transmittedAt: string | null;
  readonly contractSignedAt: string | null;
  readonly conditionsLiftedAt: string | null | undefined;
  readonly disbursedAt: string | null;
}

/** The offer read's send stamp (`transmittedAt`), the date of line 2; a dash on the screen where the read gives none. */
export function transmittedAtFrom(read: unknown): string | null {
  if (typeof read !== "object" || read === null) return null;
  const top = (read as Record<string, unknown>)["transmittedAt"];
  if (typeof top === "string") return top;
  // The offer door nests the stamp under `offer` (the validation panel reads it there too).
  const offer = (read as Record<string, unknown>)["offer"];
  if (typeof offer !== "object" || offer === null) return null;
  const nested = (offer as Record<string, unknown>)["transmittedAt"];
  return typeof nested === "string" ? nested : null;
}

/** An instant that carries a time (« 2026-10-25T10:00:00.000Z », or Postgres's « 2026-10-25 10:00:00+00 »), against a calendar day (« 2026-10-25 »). */
const HAS_TIME = /[T ]\d{2}:\d{2}/u;

/** « 25 octobre 2026 », and « , 10:00 » only where the field carries a time; a date never breaks inside itself (no-break spaces). */
export function parcoursDate(value: string | null): string {
  if (value === null) return DASH;
  const instant = new Date(value);
  if (Number.isNaN(instant.getTime())) return DASH;
  const words = HAS_TIME.test(value) ? formatDateAndTime(instant) : formatDate(instant);
  return words.replace(/ /gu, "\u00A0").replace(/,\u00A0/u, ", ");
}

/**
 * `ODF-3` (D2 §12, « Le parcours » of frame 49): five lines, one typed date each, in the record's order, the check mark on every
 * line, « - » where the file cannot give a date (the line stays), no agency. The server's `conditionsLiftedAt` is line 4; a read
 * that does not deliver it has four lines, and line 3 never absorbs it.
 */
export function ParcoursCard(props: { readonly dates: ParcoursDates }): ReactElement {
  const { dates } = props;
  const lines: readonly { readonly key: string; readonly words: string; readonly at: string | null }[] = [
    { key: "recu", words: t("bank.parcours.recu"), at: dates.submittedAt },
    { key: "offre", words: t("bank.parcours.offre"), at: dates.transmittedAt },
    { key: "signe", words: t("bank.parcours.signe"), at: dates.contractSignedAt },
    ...(dates.conditionsLiftedAt === undefined
      ? []
      : [{ key: "conditions", words: t("bank.parcours.conditions"), at: dates.conditionsLiftedAt }]),
    { key: "fonds", words: t("bank.parcours.fonds"), at: dates.disbursedAt },
  ];
  return (
    <div
      className="rounded-2xl border border-gray-200 bg-[color:var(--hk-surface)] px-5 py-4"
      data-testid="bank-parcours"
    >
      <h3 className={`mb-3 ${BANK_FS_LEAD} font-semibold leading-[1.3] ${BANK_TEXT}`}>
        {t("bank.parcours.titre")}
      </h3>
      <ol>
        {lines.map((line, index) => (
          <li
            key={line.key}
            className="relative grid grid-cols-[34px_1fr] gap-x-3 py-2"
            data-testid={`bank-parcours-ligne-${line.key}`}
          >
            {/* D2 `.chain` (frame 49): a 30 px node with a 2 px ring, the connector a 2 px line at the node's centre. */}
            {index === lines.length - 1 ? null : (
              <span
                className={`absolute -bottom-1.5 left-[14px] top-10 border-l-2 ${BANK_BORDER}`}
                aria-hidden="true"
                data-testid="bank-parcours-trait"
              />
            )}
            <span
              className="row-span-2 grid h-[30px] w-[30px] place-items-center rounded-full border-2 border-[color:var(--hk-text)] bg-[color:var(--hk-text)] text-white"
              data-testid="bank-parcours-noeud"
            >
              <Check size={14} strokeWidth={2.6} aria-hidden="true" />
            </span>
            <p className={`${BANK_FS_READ} font-semibold ${BANK_TEXT}`} data-testid="bank-parcours-libelle">
              {line.words}
            </p>
            <p
              className={`${BANK_FS_BODY} whitespace-nowrap tabular-nums ${BANK_TEXT_2}`}
              data-testid="bank-parcours-date"
            >
              {parcoursDate(line.at)}
            </p>
          </li>
        ))}
      </ol>
    </div>
  );
}
